import type { ExecutionSeriesMarker } from './normalizeExecutionsToMarkers';

type CandleForPlacement = {
  open?: number;
  high?: number;
  low?: number;
  close?: number;
};

type PlacementRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type MarkerLabelBadge = {
  id: string;
  markerTimeSec: number;
  left: number;
  top: number;
  width: number;
  height: number;
  text: string;
  color: string;
  title: string;
  anchorX: number;
  anchorY: number;
  connectorX: number;
  connectorY: number;
  pointerDirection: 'up' | 'down';
  side?: 'buy' | 'sell';
  actionType?: 'open' | 'scaleIn' | 'scaleOut' | 'close' | 'none';
};

export function layoutExecutionMarkerBadges(params: {
  markers: ExecutionSeriesMarker[];
  candleByTime: Map<number, CandleForPlacement>;
  containerWidth: number;
  containerHeight: number;
  timeToCoordinate: (time: number) => number | null;
  priceToCoordinate: (price: number) => number | null;
}): MarkerLabelBadge[] {
  const {
    markers,
    candleByTime,
    containerWidth,
    containerHeight,
    timeToCoordinate,
    priceToCoordinate,
  } = params;

  if (!Array.isArray(markers) || markers.length === 0) return [];

  const edgePadding = 10;
  const placed: Array<PlacementRect & { id: string }> = [];
  const results: MarkerLabelBadge[] = [];

  const timeDensity = new Map<number, number>();
  for (const marker of markers) {
    const t = Number(marker.time);
    if (!Number.isFinite(t)) continue;
    timeDensity.set(t, (timeDensity.get(t) ?? 0) + 1);
  }

  // Compute grouping counts for identical visible labels within the same time/side bucket
  const groupCounts = new Map<string, number>();
  for (const marker of markers) {
    const mt = Number(marker.time);
    if (!Number.isFinite(mt)) continue;
    const pos = String(marker.position ?? '').trim();
    const txt = String(marker.text ?? '').trim();
    const gk = `${mt}|${pos}|${txt}`;
    groupCounts.set(gk, (groupCounts.get(gk) ?? 0) + 1);
  }

  const stackByBucket = new Map<string, number>();

  const sorted = [...markers].sort((a, b) => {
    const ta = Number(a.time);
    const tb = Number(b.time);
    if (ta !== tb) return ta - tb;

    const ea = Number(a.executionTime ?? ta);
    const eb = Number(b.executionTime ?? tb);
    if (ea !== eb) return ea - eb;

    const sa = String(a.side ?? '');
    const sb = String(b.side ?? '');
    if (sa !== sb) return sa.localeCompare(sb);

    const ia = String(a.executionId ?? a.text ?? '');
    const ib = String(b.executionId ?? b.text ?? '');
    return ia.localeCompare(ib);
  });

  for (let index = 0; index < sorted.length; index += 1) {
    const marker = sorted[index];
    const markerTime = Number(marker.time);
    if (!Number.isFinite(markerTime)) continue;

    const xRaw = timeToCoordinate(markerTime);
    const x = Number.isFinite(Number(xRaw)) ? Number(xRaw) : null;
    if (x == null) continue;

    const candle = candleByTime.get(markerTime);
    const fallbackPrice = marker.position === 'aboveBar'
      ? Number(candle?.high)
      : marker.position === 'belowBar'
        ? Number(candle?.low)
        : Number(candle?.close);

    const anchorPrice = Number.isFinite(Number(marker.executionPrice))
      ? Number(marker.executionPrice)
      : fallbackPrice;

    if (!Number.isFinite(anchorPrice)) continue;

    const yRaw = priceToCoordinate(anchorPrice);
    const anchorY = Number.isFinite(Number(yRaw)) ? Number(yRaw) : null;
    if (anchorY == null) continue;

    const yHigh = Number.isFinite(Number(candle?.high)) ? priceToCoordinate(Number(candle?.high)) : null;
    const yLow = Number.isFinite(Number(candle?.low)) ? priceToCoordinate(Number(candle?.low)) : null;
    const labelBaseY = preferLabelBaseY({
      preferAbove: marker.position === 'aboveBar' || marker.side === 'sell',
      anchorY,
      yHigh: Number.isFinite(Number(yHigh)) ? Number(yHigh) : null,
      yLow: Number.isFinite(Number(yLow)) ? Number(yLow) : null,
    });

    const text = String(marker.text ?? '').trim();
    if (!text) continue;

    // If multiple markers share identical visible text in the same time/side bucket,
    // show a compact count suffix so users know fills were grouped/collapsed.
    const groupKey = `${markerTime}|${marker.position ?? ''}|${text}`;
    const groupCount = groupCounts.get(groupKey) ?? 1;
    const displayText = groupCount > 1 ? `${text} (${groupCount})` : text;

    const width = Math.max(44, Math.min(162, Math.round(displayText.length * 5.9 + 12)));
    const height = 15;
    const halfWidth = width / 2;

    const preferAbove = marker.position === 'aboveBar' || marker.side === 'sell';
    const sideBucket = preferAbove ? 'above' : 'below';
    const stackKey = `${markerTime}|${sideBucket}`;
    const stackIndex = stackByBucket.get(stackKey) ?? 0;
    stackByBucket.set(stackKey, stackIndex + 1);

    const xSlots = preferAbove ? [-12, 12, -20, 20, 0, -28, 28] : [12, -12, 20, -20, 0, 28, -28];
    const ySlots = preferAbove
      ? [-68, -92, -116, -140, -164, -188]
      : [56, 80, 104, 128, 152, 176];

    const rotatedYSlots = [...ySlots.slice(stackIndex), ...ySlots.slice(0, stackIndex)];

    const forbiddenRects: PlacementRect[] = [];
    const yOpen = Number.isFinite(Number(candle?.open)) ? priceToCoordinate(Number(candle?.open)) : null;
    const yClose = Number.isFinite(Number(candle?.close)) ? priceToCoordinate(Number(candle?.close)) : null;

    if (Number.isFinite(Number(yOpen)) && Number.isFinite(Number(yClose))) {
      const bodyTop = Math.min(Number(yOpen), Number(yClose)) - 6;
      const bodyBottom = Math.max(Number(yOpen), Number(yClose)) + 6;
      forbiddenRects.push({ left: x - 12, top: bodyTop, width: 24, height: Math.max(8, bodyBottom - bodyTop) });
    }

    if (Number.isFinite(Number(yHigh)) && Number.isFinite(Number(yLow))) {
      const wickTop = Math.min(Number(yHigh), Number(yLow)) - 4;
      const wickBottom = Math.max(Number(yHigh), Number(yLow)) + 4;
      forbiddenRects.push({ left: x - 6, top: wickTop, width: 12, height: Math.max(6, wickBottom - wickTop) });

      const density = timeDensity.get(markerTime) ?? 0;
      if (density >= 3) {
        forbiddenRects.push({ left: x - 26, top: wickTop - 8, width: 52, height: Math.max(10, wickBottom - wickTop + 16) });
      }
    }

    let selected: PlacementRect | null = null;
    let bestFallback: { rect: PlacementRect; score: number } | null = null;

    for (const dx of xSlots) {
      for (const dy of rotatedYSlots) {
        const centerX = clamp(x + dx, edgePadding + halfWidth, Math.max(edgePadding + halfWidth, containerWidth - edgePadding - halfWidth));
        const top = clamp(labelBaseY + dy, edgePadding, Math.max(edgePadding, containerHeight - edgePadding - height));
        const rect: PlacementRect = {
          left: centerX - halfWidth,
          top,
          width,
          height,
        };

        const overlapsLabels = placed.some((p) => intersects(rect, p));
        const overlapsCandles = forbiddenRects.some((f) => intersects(rect, f));

        if (!overlapsLabels && !overlapsCandles) {
          selected = rect;
          break;
        }

        const overlapPenalty =
          placed.reduce((acc, p) => acc + (intersects(rect, p) ? 1 : 0), 0) * 1000 +
          forbiddenRects.reduce((acc, f) => acc + (intersects(rect, f) ? 1 : 0), 0) * 700;
        const distancePenalty = Math.abs(rect.top - labelBaseY) + Math.abs((rect.left + halfWidth) - x) * 0.35;
        const score = overlapPenalty + distancePenalty;

        if (!bestFallback || score < bestFallback.score) {
          bestFallback = { rect, score };
        }
      }
      if (selected) break;
    }

    const finalRect = selected ?? bestFallback?.rect;
    if (!finalRect) continue;

    const isBelow = finalRect.top >= anchorY;
    const connectorX = clamp(x, finalRect.left + 6, finalRect.left + finalRect.width - 6);
    const connectorY = isBelow ? finalRect.top : finalRect.top + finalRect.height;

    const id = String(marker.executionId ?? `${markerTime}-${index}`);
    const titleSuffix = (groupCounts.get(`${markerTime}|${marker.position ?? ''}|${String(marker.text ?? '').trim()}`) ?? 1) > 1
      ? ` • ${groupCounts.get(`${markerTime}|${marker.position ?? ''}|${String(marker.text ?? '').trim()}`)} fills`
      : '';

    const badge: MarkerLabelBadge = {
      id,
      markerTimeSec: markerTime,
      left: finalRect.left + halfWidth,
      top: finalRect.top,
      width: finalRect.width,
      height: finalRect.height,
      text: displayText,
      color: String(marker.color ?? '#1f2937'),
      title: String((marker.fullText ?? marker.text ?? '') + titleSuffix),
      anchorX: x,
      anchorY,
      connectorX,
      connectorY,
      pointerDirection: isBelow ? 'up' : 'down',
      side: marker.side,
      actionType: marker.actionType,
    };

    placed.push({ id, ...finalRect });
    results.push(badge);
  }

  return results;
}

function preferLabelBaseY(params: {
  preferAbove: boolean;
  anchorY: number;
  yHigh: number | null;
  yLow: number | null;
}): number {
  const { preferAbove, anchorY, yHigh, yLow } = params;
  if (preferAbove && Number.isFinite(Number(yHigh))) return Number(yHigh);
  if (!preferAbove && Number.isFinite(Number(yLow))) return Number(yLow);
  return anchorY;
}

function intersects(a: PlacementRect, b: PlacementRect): boolean {
  return a.left < b.left + b.width && a.left + a.width > b.left && a.top < b.top + b.height && a.top + a.height > b.top;
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}
